import React, { createContext, useContext, useState } from 'react';
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import useExcelDownload from './useExelDownload';


const VehicleDataContext = createContext();

export const VehicleDataProvider = ({ children }) => {
    const downloadExcel = useExcelDownload();
    const [GlobalData, setGlobalData] = useState({});

    const updateVehicleData = (section, vehicleType, data) => {
        setGlobalData((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [vehicleType]: data
            }
        }));
    };

    const handleDownloadAllReport = (benchmarkType = "Custom") => {
        const flatData = downloadDataFunction(GlobalData, benchmarkType)
        downloadExcel(flatData, "Fleet Graph and Insight Data")
    };

    return (
        <VehicleDataContext.Provider value={{ GlobalData, updateVehicleData, setGlobalData, handleDownloadAllReport }}>
            {children}
        </VehicleDataContext.Provider>
    );
};

export const useVehicleData = () => useContext(VehicleDataContext);


const downloadDataFunction = (GlobalData, benchmarkType) => {
    const labelMap = {
        "Date": "Date",
        "Vin": "Vin",
        "Modal": "Modal",
        "REG": "REG",
        "Energy Efficiency": "Energy Efficiency",
        "Energy Efficiency Score": "Energy Efficiency Score",
        "distance": "Distance",
        "Energy Consumed": "Energy Consumed",
        "Vehicle Operating Hours": "Vehicle Operating Hours",
        "Vehicle Utilization": "Vehicle Utilization",
        "Average Speed": "Average Speed",
        "Vehicle Speed Distribution": "Vehicle Speed Distribution",
        "Acc. Pedal Position": "Acc. Pedal Position",
        "Hash Acceleration": "Hash Acceleration",
        "Hash Breaking": "Hash Breaking",
        "Rash Turning": "Rash Turning",
        "Over Speeding": "Over Speeding",
        "Driving Score": "Driving Score",
        "Idling": "Idling",
        "Coasting": "Coasting",
        "Regeneration": "Regeneration",
        "RPM": "RPM",
        "AC Consumption": "AC Consumption",

        "Coolant Low": "Coolant Low",
        "EV Fault Indication": "EV Fault Indication",
        "High Temperature Indication": "High Temperature Indication",
        "Low SoC Warning": "Low SoC Warning",
        "Service Indication": "Service Indication"
    };

    const primaryVinData = {
        "Date": GlobalData?.globalFilter?.startDate + " - " + GlobalData?.globalFilter?.endDate,
        "Vin": GlobalData?.primaryVehicleDetails?.vehicleId,
        "Modal": GlobalData?.primaryVehicleDetails?.LOB + " " + GlobalData?.primaryVehicleDetails?.PL,
        "REG": GlobalData?.primaryVehicleDetails?.vehicle_registration_number,
        "Energy Efficiency": GlobalData?.energyEfficiency?.energyEfficiency?.primaryVehicle + " KWh/Km",
        "Energy Efficiency Score": GlobalData?.energyEfficiency?.energyEfficiencyScore?.primaryVehicle,
        "distance": GlobalData?.runningHistory?.distance?.primaryVehicle + " Km",
        "Energy Consumed": GlobalData?.runningHistory?.energyConsumed?.primaryVehicle + " KWh/Km",
        "Vehicle Operating Hours": GlobalData?.runningHistory?.vehicleOperatingHours?.primaryVehicle,
        "Vehicle Utilization": GlobalData?.runningHistory?.vehicleUtilization?.primaryVehicle + " %",

        "Average Speed": GlobalData?.drivingBehavior?.primaryVehicleData?.averageSpeed + " Km/hr",
        "Vehicle Speed Distribution": GlobalData?.drivingBehavior?.primaryVehicleData?.vehicleSpeedDistribution + " %",
        "Acc. Pedal Position": GlobalData?.drivingBehavior?.primaryVehicleData?.accPedalPosition + " %",
        "Hash Acceleration": GlobalData?.drivingBehavior?.primaryVehicleData?.harshAcceleration + "",
        "Hash Breaking": GlobalData?.drivingBehavior?.primaryVehicleData?.harshBreaking + "",
        "Rash Turning": GlobalData?.drivingBehavior?.primaryVehicleData?.rushTurning + "",
        "Over Speeding": GlobalData?.drivingBehavior?.primaryVehicleData?.overSpeeding + "",
        "Driving Score": GlobalData?.drivingBehavior?.primaryVehicleData?.drivingScore + " %",
        "Idling": GlobalData?.drivingBehavior?.primaryVehicleData?.idling + " %",
        "Coasting": GlobalData?.drivingBehavior?.primaryVehicleData?.coasting + " %",
        "Regeneration": GlobalData?.drivingBehavior?.primaryVehicleData?.regeneration + " %",
        "RPM": GlobalData?.drivingBehavior?.primaryVehicleData?.RPM + " %",
        "AC Consumption": GlobalData?.drivingBehavior?.primaryVehicleData?.AC_Consumption + "",

        "Coolant Low": GlobalData?.vehicleHealthParameter?.coolantlow?.primaryVehicle + " %",
        "EV Fault Indication": GlobalData?.vehicleHealthParameter?.evfaultindication?.primaryVehicle + " %",
        "High Temperature Indication": GlobalData?.vehicleHealthParameter?.highTempIndication?.primaryVehicle + " %",
        "Low SoC Warning": GlobalData?.vehicleHealthParameter?.lowSoCWarning?.primaryVehicle + " %",
        "Service Indication": GlobalData?.vehicleHealthParameter?.waterInFuelIndicator?.primaryVehicle + " %",
    };

    const bestVinData = {
        "Date": GlobalData?.bestVehicle,
        "Vin": GlobalData?.primaryVehicleDetails?.vehicleId,
        "Modal": GlobalData?.primaryVehicleDetails?.LOB + " " + GlobalData?.primaryVehicleDetails?.PL,
        "REG": GlobalData?.primaryVehicleDetails?.vehicle_registration_number,
        "Energy Efficiency": GlobalData?.energyEfficiency?.energyEfficiency?.bestVehicle + " KWh/Km",
        "Energy Efficiency Score": GlobalData?.energyEfficiency?.energyEfficiencyScore?.bestVehicle,
        "distance": GlobalData?.runningHistory?.distance?.bestVehicle + " Km",
        "Energy Consumed": GlobalData?.runningHistory?.energyConsumed?.bestVehicle + " KWh/Km",
        "Vehicle Operating Hours": GlobalData?.runningHistory?.vehicleOperatingHours?.bestVehicle,
        "Vehicle Utilization": GlobalData?.runningHistory?.vehicleUtilization?.bestVehicle + " %",
        "Average Speed": GlobalData?.drivingBehavior?.bestVehicleData?.averageSpeed + " Km/hr",
        "Vehicle Speed Distribution": GlobalData?.drivingBehavior?.bestVehicleData?.vehicleSpeedDistribution + " %",
        "Acc. Pedal Position": GlobalData?.drivingBehavior?.bestVehicleData?.accPedalPosition + " %",
        "Hash Acceleration": GlobalData?.drivingBehavior?.bestVehicleData?.harshAcceleration + "",
        "Hash Breaking": GlobalData?.drivingBehavior?.bestVehicleData?.harshBreaking + "",
        "Rash Turning": GlobalData?.drivingBehavior?.bestVehicleData?.rushTurning + "",
        "Over Speeding": GlobalData?.drivingBehavior?.bestVehicleData?.overSpeeding + "",
        "Driving Score": GlobalData?.drivingBehavior?.bestVehicleData?.drivingScore + " %",
        "Idling": GlobalData?.drivingBehavior?.bestVehicleData?.idling + " %",
        "Coasting": GlobalData?.drivingBehavior?.bestVehicleData?.coasting + " %",
        "Regeneration": GlobalData?.drivingBehavior?.bestVehicleData?.regeneration + " %",
        "RPM": GlobalData?.drivingBehavior?.bestVehicleData?.RPM + " %",
        "AC Consumption": GlobalData?.drivingBehavior?.bestVehicleData?.AC_Consumption + "",

        "Coolant Low": GlobalData?.vehicleHealthParameter?.coolantlow?.bestVehicle + " %",
        "EV Fault Indication": GlobalData?.vehicleHealthParameter?.evfaultindication?.bestVehicle + " %",
        "High Temperature Indication": GlobalData?.vehicleHealthParameter?.highTempIndication?.bestVehicle + " %",
        "Low SoC Warning": GlobalData?.vehicleHealthParameter?.lowSoCWarning?.bestVehicle + " %",
        "Service Indication": GlobalData?.vehicleHealthParameter?.waterInFuelIndicator?.bestVehicle + " %",
    };

    // Cutom
    const secondaryVinData = {
        "Date": GlobalData?.customFilter?.startDate + " - " + GlobalData?.customFilter?.endDate,
        "Vin": GlobalData?.customVehicleDetails?.vehicleId,
        "Modal": GlobalData?.customVehicleDetails?.LOB + " " + GlobalData?.customVehicleDetails?.PL,
        "REG": GlobalData?.customVehicleDetails?.vehicle_registration_number,
        "Energy Efficiency": GlobalData?.energyEfficiency?.energyEfficiency?.secondaryVehicle + " KWh/Km",
        "Energy Efficiency Score": GlobalData?.energyEfficiency?.energyEfficiencyScore?.secondaryVehicle,
        "distance": GlobalData?.runningHistory?.distance?.secondaryVehicle + " Km",
        "Energy Consumed": GlobalData?.runningHistory?.energyConsumed?.secondaryVehicle + " KWh/Km",
        "Vehicle Operating Hours": GlobalData?.runningHistory?.vehicleOperatingHours?.secondaryVehicle,
        "Vehicle Utilization": GlobalData?.runningHistory?.vehicleUtilization?.secondaryVehicle + " %",

        "Average Speed": GlobalData?.drivingBehavior?.secondaryVehicleData?.averageSpeed + " Km/hr",
        "Vehicle Speed Distribution": GlobalData?.drivingBehavior?.secondaryVehicleData?.vehicleSpeedDistribution + " %",
        "Acc. Pedal Position": GlobalData?.drivingBehavior?.secondaryVehicleData?.accPedalPosition + " %",
        "Hash Acceleration": GlobalData?.drivingBehavior?.secondaryVehicleData?.harshAcceleration + "",
        "Hash Breaking": GlobalData?.drivingBehavior?.secondaryVehicleData?.harshBreaking + "",
        "Rash Turning": GlobalData?.drivingBehavior?.secondaryVehicleData?.rushTurning + "",
        "Over Speeding": GlobalData?.drivingBehavior?.secondaryVehicleData?.overSpeeding + "",
        "Driving Score": GlobalData?.drivingBehavior?.secondaryVehicleData?.drivingScore + " %",
        "Idling": GlobalData?.drivingBehavior?.secondaryVehicleData?.idling + " %",
        "Coasting": GlobalData?.drivingBehavior?.secondaryVehicleData?.coasting + " %",
        "Regeneration": GlobalData?.drivingBehavior?.secondaryVehicleData?.regeneration + " %",
        "RPM": GlobalData?.drivingBehavior?.secondaryVehicleData?.RPM + " %",
        "AC Consumption": GlobalData?.drivingBehavior?.secondaryVehicleData?.AC_Consumption + "",

        "Coolant Low": GlobalData?.vehicleHealthParameter?.coolantlow?.secondaryVehicle + " %",
        "EV Fault Indication": GlobalData?.vehicleHealthParameter?.evfaultindication?.secondaryVehicle + " %",
        "High Temperature Indication": GlobalData?.vehicleHealthParameter?.highTempIndication?.secondaryVehicle + " %",
        "Low SoC Warning": GlobalData?.vehicleHealthParameter?.lowSoCWarning?.secondaryVehicle + " %",
        "Service Indication": GlobalData?.vehicleHealthParameter?.waterInFuelIndicator?.secondaryVehicle + " %",
    };

    // National
    const nationalVinData = {
        "Date": GlobalData?.globalFilter?.startDate + " - " + GlobalData?.globalFilter?.endDate,
        "Vin": GlobalData?.nationalVehicleDetails?.vehicleId,
        "Modal": GlobalData?.nationalVehicleDetails?.LOB + " " + GlobalData?.nationalVehicleDetails?.PL,
        "REG": GlobalData?.nationalVehicleDetails?.vehicle_registration_number,
        "Energy Efficiency": GlobalData?.energyEfficiency?.energyEfficiency?.nationalVehicle + " KWh/Km",
        "Energy Efficiency Score": GlobalData?.energyEfficiency?.energyEfficiencyScore?.nationalVehicle,
        "distance": GlobalData?.runningHistory?.distance?.nationalVehicle + " Km",
        "Energy Consumed": GlobalData?.runningHistory?.energyConsumed?.nationalVehicle + " KWh/Km",
        "Vehicle Operating Hours": GlobalData?.runningHistory?.vehicleOperatingHours?.nationalVehicle,
        "Vehicle Utilization": GlobalData?.runningHistory?.vehicleUtilization?.nationalVehicle + " %",

        "Average Speed": GlobalData?.drivingBehavior?.nationalVehicleData?.averageSpeed + " Km/hr",
        "Vehicle Speed Distribution": GlobalData?.drivingBehavior?.nationalVehicleData?.vehicleSpeedDistribution + " %",
        "Acc. Pedal Position": GlobalData?.drivingBehavior?.nationalVehicleData?.accPedalPosition + " %",
        "Hash Acceleration": GlobalData?.drivingBehavior?.nationalVehicleData?.harshAcceleration + "",
        "Hash Breaking": GlobalData?.drivingBehavior?.nationalVehicleData?.harshBreaking + "",
        "Rash Turning": GlobalData?.drivingBehavior?.nationalVehicleData?.rushTurning + "",
        "Over Speeding": GlobalData?.drivingBehavior?.nationalVehicleData?.overSpeeding + "",
        "Driving Score": GlobalData?.drivingBehavior?.nationalVehicleData?.drivingScore + " %",
        "Idling": GlobalData?.drivingBehavior?.nationalVehicleData?.idling + " %",
        "Coasting": GlobalData?.drivingBehavior?.nationalVehicleData?.coasting + " %",
        "Regeneration": GlobalData?.drivingBehavior?.nationalVehicleData?.regeneration + " %",
        "RPM": GlobalData?.drivingBehavior?.nationalVehicleData?.RPM + " %",
        "AC Consumption": GlobalData?.drivingBehavior?.nationalVehicleData?.AC_Consumption + "",

        "Coolant Low": GlobalData?.vehicleHealthParameter?.coolantlow?.nationalVehicle + " %",
        "EV Fault Indication": GlobalData?.vehicleHealthParameter?.evfaultindication?.nationalVehicle + " %",
        "High Temperature Indication": GlobalData?.vehicleHealthParameter?.highTempIndication?.nationalVehicle + " %",
        "Low SoC Warning": GlobalData?.vehicleHealthParameter?.lowSoCWarning?.nationalVehicle + " %",
        "Service Indication": GlobalData?.vehicleHealthParameter?.waterInFuelIndicator?.nationalVehicle + " %",
    };


    //     "Date": benchmarkType === "Custom" ? GlobalData?.customFilter?.startDate + " - " + GlobalData?.customFilter?.endDate : GlobalData?.globalFilter?.startDate + " - " + GlobalData?.globalFilter?.endDate,
    //     "Vin": GlobalData?.secondVin?.vehicleId,
    //     "Modal": "Modal",
    //     "REG": GlobalData?.firstVin?.regNo,
    //     "Energy Efficiency": GlobalData?.energyEfficiency?.energyEfficiency?.nationalVehicle + " KWh/Km",
    //     "Energy Efficiency Score": GlobalData?.energyEfficiency?.energyEfficiencyScore?.nationalVehicle,
    //     "distance": GlobalData?.runningHistory?.distance?.nationalVehicle + " Km",
    //     "Energy Consumed": GlobalData?.runningHistory?.energyConsumed?.nationalVehicle + " KWh/Km",
    //     "Vehicle Operating Hours": GlobalData?.runningHistory?.vehicleOperatingHours?.nationalVehicle,
    //     "Vehicle Utilization": GlobalData?.runningHistory?.vehicleUtilization?.nationalVehicle + " %",

    //     "Average Speed": GlobalData?.drivingBehavior?.nationalVehicleData?.averageSpeed + " Km/hr",
    //     "Vehicle Speed Distribution": GlobalData?.drivingBehavior?.nationalVehicleData?.vehicleSpeedDistribution + " %",
    //     "Acc. Pedal Position": GlobalData?.drivingBehavior?.nationalVehicleData?.accPedalPosition + " %",
    //     "Hash Acceleration": GlobalData?.drivingBehavior?.nationalVehicleData?.harshAcceleration + "",
    //     "Hash Breaking": GlobalData?.drivingBehavior?.nationalVehicleData?.harshBreaking + "",
    //     "Rash Turning": GlobalData?.drivingBehavior?.nationalVehicleData?.rushTurning + "",
    //     "Over Speeding": GlobalData?.drivingBehavior?.nationalVehicleData?.overSpeeding + "",
    //     "Driving Score": GlobalData?.drivingBehavior?.nationalVehicleData?.drivingScore + " %",
    //     "Idling": GlobalData?.drivingBehavior?.nationalVehicleData?.idling + " %",
    //     "Coasting": GlobalData?.drivingBehavior?.nationalVehicleData?.coasting + " %",
    //     "Regeneration": GlobalData?.drivingBehavior?.nationalVehicleData?.regeneration + " %",
    //     "RPM": GlobalData?.drivingBehavior?.nationalVehicleData?.RPM + " %",
    //     "AC Consumption": GlobalData?.drivingBehavior?.nationalVehicleData?.AC_Consumption + "",

    //     "Coolant Low": GlobalData?.vehicleHealthParameter?.vehicleUtilization?.primaryVehicle + " %",
    //     "EV Fault Indication": GlobalData?.vehicleHealthParameter?.vehicleUtilization?.primaryVehicle + " %",
    //     "High Temperature Indication": GlobalData?.vehicleHealthParameter?.vehicleUtilization?.primaryVehicle + " %",
    //     "Low SoC Warning": GlobalData?.vehicleHealthParameter?.vehicleUtilization?.primaryVehicle + " %",
    //     "Service Indication": GlobalData?.vehicleHealthParameter?.vehicleUtilization?.primaryVehicle + " %",
    // };

    // Depot depoVehicleDetails
    const depotVinData = {
        "Date": GlobalData?.globalFilter?.startDate + " - " + GlobalData?.globalFilter?.endDate,
        "Vin": GlobalData?.depoVehicleDetails?.vehicleId,
        "Modal": GlobalData?.depoVehicleDetails?.LOB + " " + GlobalData?.depoVehicleDetails?.PL,
        "REG": GlobalData?.depoVehicleDetails?.vehicle_registration_number,
        "Energy Efficiency": GlobalData?.energyEfficiency?.energyEfficiency?.depotVehicle + " KWh/Km",
        "Energy Efficiency Score": GlobalData?.energyEfficiency?.energyEfficiencyScore?.depotVehicle,
        "distance": GlobalData?.runningHistory?.distance?.depotVehicle + " Km",
        "Energy Consumed": GlobalData?.runningHistory?.energyConsumed?.depotVehicle + " KWh/Km",
        "Vehicle Operating Hours": GlobalData?.runningHistory?.vehicleOperatingHours?.depotVehicle,
        "Vehicle Utilization": GlobalData?.runningHistory?.vehicleUtilization?.depotVehicle + " %",

        "Average Speed": GlobalData?.drivingBehavior?.depotVehicleData?.averageSpeed + " Km/hr",
        "Vehicle Speed Distribution": GlobalData?.drivingBehavior?.depotVehicleData?.vehicleSpeedDistribution + " %",
        "Acc. Pedal Position": GlobalData?.drivingBehavior?.depotVehicleData?.accPedalPosition + " %",
        "Hash Acceleration": GlobalData?.drivingBehavior?.depotVehicleData?.harshAcceleration + "",
        "Hash Breaking": GlobalData?.drivingBehavior?.depotVehicleData?.harshBreaking + "",
        "Rash Turning": GlobalData?.drivingBehavior?.depotVehicleData?.rushTurning + "",
        "Over Speeding": GlobalData?.drivingBehavior?.depotVehicleData?.overSpeeding + "",
        "Driving Score": GlobalData?.drivingBehavior?.depotVehicleData?.drivingScore + " %",
        "Idling": GlobalData?.drivingBehavior?.depotVehicleData?.idling + " %",
        "Coasting": GlobalData?.drivingBehavior?.depotVehicleData?.coasting + " %",
        "Regeneration": GlobalData?.drivingBehavior?.depotVehicleData?.regeneration + " %",
        "RPM": GlobalData?.drivingBehavior?.depotVehicleData?.RPM + " %",
        "AC Consumption": GlobalData?.drivingBehavior?.depotVehicleData?.AC_Consumption + "",

        "Coolant Low": GlobalData?.vehicleHealthParameter?.coolantlow?.depotVehicle + " %",
        "EV Fault Indication": GlobalData?.vehicleHealthParameter?.evfaultindication?.depotVehicle + " %",
        "High Temperature Indication": GlobalData?.vehicleHealthParameter?.highTempIndication?.depotVehicle + " %",
        "Low SoC Warning": GlobalData?.vehicleHealthParameter?.lowSoCWarning?.depotVehicle + " %",
        "Service Indication": GlobalData?.vehicleHealthParameter?.waterInFuelIndicator?.depotVehicle + " %",
    };

    const flatData = Object.keys(labelMap).map((key) => ({
        Parameters: labelMap[key],
        "Selected Vehicle": primaryVinData[key],
        "Selected Vehicle Best Driven Day": bestVinData[key],
        "National": nationalVinData[key],
        "Depot": depotVinData[key],
        "Custom (If any)": GlobalData?.customVehicleDetails?.vehicleId ? secondaryVinData?.[key] : ''
    }));

    return flatData
}
